import React, { useEffect } from "react";
import { Grid, Button } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";

const INITIAL_STATE = {
    account: "",
    entity: "",
    shipmentId: "",
    transitBranch: null,
};

const ShipmentTransitBranchForm = ({
    isBusy = false,
    shipmentId,
    onSubmit = () => { },
    onCancel = () => { },
}) => {
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
    const { ShipmentDetail } = useShipmentDetailContext();

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity,
            account: viewEntity?.entity?.account,
            shipmentId: ShipmentDetail?._id,
            transitBranch: null,
        },
    });

    const { data: accounts = [], isLoading: loadingAccounts } =
        useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();
    const [fetchBranches, { data: branches = [], isLoading: loadingBranches }] =
        useLazyGetDDLBranchListQuery();

    const watchAccount = watch("account");
    const watchEntity = watch("entity");

    // Fetch entities
    useEffect(() => {
        if ((isAccountAdmin || isSystemAdmin) && watchAccount?._id) {
            fetchEntities({ account: watchAccount._id });
        }
    }, [watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

    // Fetch branches
    useEffect(() => {
        if (watchEntity?._id) {
            fetchBranches({ account: watchAccount?._id, entity: watchEntity._id });
        }
    }, [watchAccount?._id, watchEntity?._id, fetchBranches]);

    const handleOnSubmit = (data) => {
        const payload = {
            shipmentId: ShipmentDetail?._id,
            transitBranch: data.transitBranch?._id,
        };

        onSubmit(payload);
    };

    return (
        <AppForm loading={isBusy}>
            <AppForm.Content>
                <Grid container spacing={4}>
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item md={12}>
                            <Controller
                                name="account"
                                control={control}
                                rules={{ required: "Please select an account." }}
                                render={({ field }) => (
                                    <BaseAutocomplete
                                        {...field}
                                        fullWidth
                                        label="Account"
                                        required
                                        value={field.value ?? null}
                                        onChange={(_, data) => {
                                            field.onChange(data);
                                            setValue("entity", null);
                                            setValue("transitBranch", null);
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id
                                        }
                                        options={accounts}
                                        loading={loadingAccounts}
                                        helperText={errors?.account?.message}
                                        error={!!errors?.account}
                                        disabled
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item md={12}>
                            <Controller
                                name="entity"
                                control={control}
                                rules={{ required: "Please select an entity." }}
                                render={({ field }) => (
                                    <BaseAutocomplete
                                        {...field}
                                        fullWidth
                                        label="Entity"
                                        required
                                        value={field.value ?? null}
                                        onChange={(_, data) => {
                                            field.onChange(data);
                                            setValue("transitBranch", null);
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id
                                        }
                                        options={entities}
                                        loading={loadingEntities}
                                        helperText={errors?.entity?.message}
                                        error={!!errors?.entity}
                                        disabled
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    <Grid item md={12}>
                        <Controller
                            name="transitBranch"
                            control={control}
                            rules={{ required: "Please select a transit branch." }}
                            render={({ field }) => (
                                <BaseAutocomplete
                                    {...field}
                                    fullWidth
                                    label="Transit Branch"
                                    required
                                    value={field.value ?? null}
                                    onChange={(_, data) => field.onChange(data)}
                                    getOptionLabel={(option) =>
                                        `${option?.name ?? ""} - ${option?.code ?? ""}`
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={branches}
                                    loading={loadingBranches}
                                    helperText={errors?.transitBranch?.message}
                                    error={!!errors?.transitBranch}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </AppForm.Content>
            <AppForm.Actions>
                <Button disabled={isBusy} onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isBusy}
                    onClick={handleSubmit(handleOnSubmit)}
                >
                    Add
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default ShipmentTransitBranchForm;